import { List, Map } from "immutable";

import { type BaseSpec, type ViewColumnDef, type ViewSpec } from "@moment/api-collab/api-types";

import { QueryEvaluator } from "../QueryEvaluator";
import {
	type ActionIntent,
	type ColumnAppendActionIntent,
	type ColumnDeleteActionIntent,
} from "../QueryEvaluator/ActionIntent";
import { type QueryEvaluatorState } from "../QueryEvaluator/QueryManagerState";
import { type TableMutation } from "../QueryEvaluator/TableMutation";
import { Tuple, idOf } from "../Tuple";
import { deleteChange, insert } from "../Tuples";
import { type JsonPathLiteral } from "../jsonTable/JsonPathLiteral";
import { type TopLevelQuerySpec, querySpecId } from "../operatorsV2/QuerySpec";
import { tupleComparatorFromOrderBySpec } from "../operatorsV2/orderedTopK";
import { type DatabaseTables, type TableName } from "../types";
import { type BaseSpecPersistence } from "./BaseSpecPersistence";
import { Query } from "./Query";
import { type QueryPersistence } from "./QueryPersistence";

type Subscriber<T> = {
	onChange: (results: T[], changes: TableMutation<T>[]) => void;
	subscriptionKey: string;
};

type MetadataSubscriber = {
	onChange: (baseSpec: BaseSpec) => void;
	subscriptionKey: string;
};

export type Subscription = () => void;

export interface QueryClient<Tables extends DatabaseTables> {
	from<T extends TableName<Tables>>(table: T): Query<Tables, T>;
	pendingActions: List<ActionIntent>;
	subscribeQuery<T>(
		query: TopLevelQuerySpec,
		onChange: (results: T[], changes: TableMutation<T>[]) => void
	): Subscription;
	subscribeMetadata(onChange: (baseSpec: BaseSpec) => void): Subscription;
	insert(
		tableId: TableName<Tables>,
		id: string,
		thing: Tables[TableName<Tables>]
	): Promise<QueryEvaluatorState>;
	delete(tableId: TableName<Tables>, id: string): Promise<QueryEvaluatorState>;
}

export interface BaseSpecClient {
	updateView(viewId: string, viewSpec: ViewSpec): Promise<void>;
}

export class Store<Tables extends DatabaseTables> implements QueryClient<Tables>, BaseSpecClient {
	private _evaluator: QueryEvaluator;
	private _queryPersistence: QueryPersistence;
	private _baseSpecPersistence: BaseSpecPersistence;
	private _subscriptions: Map<string, List<Subscriber<unknown>>>;
	private _metadataSubscriptions: List<MetadataSubscriber>;

	constructor(queryPersistence: QueryPersistence, baseSpecPersistence: BaseSpecPersistence) {
		// FIXME: This should be table specs.
		this._evaluator = new QueryEvaluator(
			queryPersistence.localStateReplica().tables.toObject()
		);
		this._queryPersistence = queryPersistence;
		this._baseSpecPersistence = baseSpecPersistence;
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());
		this._subscriptions = Map();
		this._metadataSubscriptions = List();
	}

	// Start BaseSpecClient implementation ////////////////////////////////////////////////////////

	view(viewId: string): ViewSpec | undefined {
		return this._baseSpecPersistence.localBaseSpecReplica().views[viewId];
	}

	async updateView(viewId: string, viewSpec: ViewSpec): Promise<void> {
		const newBaseSpec = structuredClone(this._baseSpecPersistence.localBaseSpecReplica());
		newBaseSpec.views[viewId] = viewSpec;
		await this._baseSpecPersistence.saveBaseSpec(newBaseSpec);
		this._notifyMetadataSubscribers(newBaseSpec);
	}

	async appendColumn(viewId: string, column: ViewColumnDef): Promise<ColumnAppendActionIntent> {
		const view = this.view(viewId);
		if (!view) {
			throw new Error(`View ${viewId} not found`);
		}
		const newColumns = [...view.columns, column];
		await this.updateView(viewId, { ...view, columns: newColumns });

		const appendIntent = this._evaluator.columnAppendIntent(view.query.spec[0].table, column);

		await this._queryPersistence.ensurePersistedState(appendIntent);
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());
		this._notifyMetadataSubscribers(this._baseSpecPersistence.localBaseSpecReplica());
		return appendIntent;
	}

	async deleteColumn(viewId: string, column: ViewColumnDef): Promise<ColumnDeleteActionIntent> {
		const view = this.view(viewId);
		if (!view) {
			throw new Error(`View ${viewId} not found`);
		}
		const newColumns = view.columns.filter((c) => c.accessorKey !== column.accessorKey);
		await this.updateView(viewId, { ...view, columns: newColumns });

		const deleteIntent = this._evaluator.columnDeleteIntent(view.query.spec[0].table, column);

		await this._queryPersistence.ensurePersistedState(deleteIntent);
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());
		this._notifyMetadataSubscribers(this._baseSpecPersistence.localBaseSpecReplica());
		return deleteIntent;
	}

	async renameColumn(
		viewId: string,
		oldColumnAccessorKey: JsonPathLiteral,
		newHeader: string,
		newAccessorKey: JsonPathLiteral
	): Promise<void> {
		const view = this.view(viewId);
		if (!view) {
			throw new Error(`View ${viewId} not found`);
		}

		// Find and update the column in the view
		const index = view.columns.findIndex((c) => c.accessorKey === oldColumnAccessorKey);
		if (index === -1) {
			throw new Error(`Column with accessor key ${oldColumnAccessorKey} not found`);
		}

		const oldColumn = view.columns[index]!;
		const newColumn = { ...oldColumn, header: newHeader, accessorKey: newAccessorKey };
		const newColumns = [...view.columns];
		newColumns[index] = newColumn;

		// Update the view with the new column
		await this.updateView(viewId, { ...view, columns: newColumns });

		// Create and persist the column rename intent
		const renameIntent = this._evaluator.columnRenameIntent(
			view.query.spec[0].table,
			oldColumnAccessorKey,
			newHeader,
			newAccessorKey
		);

		await this._queryPersistence.ensurePersistedState(renameIntent);
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());
		this._notifyQuerySubscribers(renameIntent.changesByQueryId, renameIntent.proposedState);
		this._notifyMetadataSubscribers(this._baseSpecPersistence.localBaseSpecReplica());
	}

	// End BaseSpecClient implementation //////////////////////////////////////////////////////////

	// Start QueryClient implementation ///////////////////////////////////////////////////////////

	from<T extends TableName<Tables>>(table: T): Query<Tables, T> {
		return new Query<Tables, T>(this, table);
	}

	get pendingActions(): List<ActionIntent> {
		return this._evaluator.pendingActions;
	}

	async insert(tableId: TableName<Tables>, id: string, thing: Tables[TableName<Tables>]) {
		return this.applyMutation(tableId, insert(Tuple.from<unknown>(id, thing)));
	}

	async delete(tableId: TableName<Tables>, id: string) {
		return this.applyMutation(tableId, deleteChange(id));
	}

	subscribeQuery<T>(
		query: TopLevelQuerySpec,
		onChange: (results: T[], changes: TableMutation<T>[]) => void
	): Subscription {
		const subscriptionKey = crypto.randomUUID();
		const queryId = querySpecId(query.spec).contentId;
		const subscriber: Subscriber<T> = { onChange, subscriptionKey };

		// Add subscriber to the map
		this._subscriptions = this._subscriptions.update(queryId, (subscribers = List()) =>
			subscribers.push(subscriber as Subscriber<unknown>)
		);

		// Register the query and get initial results
		void this.registerQuery(query).then((results) => {
			// FIXME. All of this is insane lol.
			const { proposedState } = results;
			const lastResults = proposedState.queryResults.get(queryId)?.toArray();
			if (!lastResults || lastResults.length === 0) {
				return;
			}

			const resultsAsChange = List(lastResults.map(({ tuple }) => insert(tuple)));
			this._notifyQuerySubscribers(Map([[queryId, resultsAsChange]]), proposedState);
		});

		// Return unsubscribe function
		return () => {
			// Remove this subscriber from the map
			this._subscriptions = this._subscriptions.update(queryId, (subscribers) => {
				if (!subscribers) {
					return List();
				}
				return subscribers.filter((s) => s.subscriptionKey !== subscriptionKey);
			});

			// If no more subscribers for this query, unregister it
			if (this._subscriptions.get(queryId)?.isEmpty()) {
				// FIXME: Implement query unregistration
			}
		};
	}

	subscribeMetadata(onChange: (baseSpec: BaseSpec) => void): Subscription {
		const subscriptionKey = crypto.randomUUID();
		const subscriber: MetadataSubscriber = { onChange, subscriptionKey };

		this._metadataSubscriptions = this._metadataSubscriptions.push(subscriber);
		onChange(this._baseSpecPersistence.localBaseSpecReplica());

		// Return unsubscribe function
		return () => {
			this._metadataSubscriptions = this._metadataSubscriptions.filter(
				(sub) => sub.subscriptionKey !== subscriptionKey
			);
		};
	}

	// End QueryClient implementation /////////////////////////////////////////////////////////////

	private async registerQuery(query: TopLevelQuerySpec) {
		const proposedState = this._evaluator.queryIntent(query);
		await this._queryPersistence.ensurePersistedState(proposedState);
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());
		return proposedState;
	}

	async applyMutation(
		tableId: string,
		mutation: TableMutation<unknown>
	): Promise<QueryEvaluatorState> {
		const lastSeenEpoch = this._evaluator.latestState.currentEpoch;
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());
		const mutationIntent = this._evaluator.mutationIntent(tableId, lastSeenEpoch, mutation);
		const { proposedState, changesByQueryId } = mutationIntent;
		this._notifyQuerySubscribers(changesByQueryId, proposedState);

		await this._queryPersistence.ensurePersistedState(mutationIntent);
		this._evaluator.ensureReconciled(this._queryPersistence.localStateReplica());

		return proposedState;
	}

	private _notifyMetadataSubscribers(baseSpec: BaseSpec) {
		this._metadataSubscriptions.forEach((subscriber) => {
			subscriber.onChange(baseSpec);
		});
	}

	private _notifyQuerySubscribers(
		changesByQueryId: Map<string, List<TableMutation<unknown>>>,
		state: QueryEvaluatorState
	) {
		for (const [queryId, changes] of changesByQueryId) {
			const subscribers = this._subscriptions.get(queryId);
			if (!subscribers) {
				continue;
			}
			const querySpec = state.querySpecs.get(queryId);
			const queryState = state.queryStates.get(queryId);
			const queryResults = state.queryResults.get(queryId);
			if (!querySpec || !queryState || !queryResults) {
				continue;
			}

			//
			// FIXME: we should do this server-side. ℤ-sets do not represent ordering, so if there
			// is an ordered top-k "finalizer", we need to sort the results before sending them to
			// the subscriber.
			//

			let finalizedResults = queryResults;
			if (querySpec.finalizerSpec) {
				finalizedResults = queryResults.sort(
					tupleComparatorFromOrderBySpec(querySpec.finalizerSpec),
					querySpec.finalizerSpec?.direction ?? "ASC"
				);
			} else {
				// FIXME: Horribly inefficient.
				finalizedResults = queryResults.sort(
					(a) => Tuple.from(a.id, parseInt(idOf(a)!)),
					"ASC"
				);
			}

			subscribers.forEach((subscriber) => {
				subscriber.onChange(
					finalizedResults
						.toArray()
						.flatMap(({ tuple, weight }) =>
							weight > 0 ? [tuple.getValue() as unknown] : []
						),
					changes.toArray()
				);
			});
		}
	}
}

// // Usage:
// const db = new Store({ directors: {}, movies: {} });
// db.from("directors")
// 	.where("$.name", "=", "Peter Jackson")
// 	.select({
// 		"$.films": ({ jsonGroupArray, valAtPath }) =>
// 			db
// 				.from("movies")
// 				.where("$.director.name", "=", valAtPath("directors", "$.name"))
// 				.orderBy("$.year")
// 				.select(jsonGroupArray("movies", "$")),
// 	});

// const query = db
// 	.from("directors")
// 	.where(({ eq, pluck }) => eq(pluck("$.name"), "Peter Jackson"))
// 	.select({
// 		"$.films": ({ jsonGroupArray, eq, pluck }) =>
// 			db
// 				.from("movies")
// 				.where(eq(pluck("$.director.name"), pluck("directors", "$.name")))
// 				.orderBy(pluck("$.year"))
// 				.select(jsonGroupArray("movies", "$")),
// 	});
